<template>
  <div
    ref="actionContainer"
    class="c-chat__dialogMainContentMessageActions o-flex -start -gap1"
    :class="{ '-replyingToMessage': chatThreadStore.replyingToMessage }">
    <div
      v-if="isInactive"
      class="c-chat__inactiveMessage t-caption1">
      {{ $t('chat.thread_inactive', { username: otherDisplayName }) }}
    </div>
    <template v-else>
      <div class="o-flex -start -gap2">
        <div class="o-flex__item -grow">
          <div
            class="s-chatMessageActions__inputWrapper"
            :class="{ '-replying': chatThreadStore.replyingToMessage }">
            <ReplyPreview
              v-if="chatThreadStore.replyingToMessage"
              :message="chatThreadStore.replyingToMessage"
              :username="replyUsername"
              :show-cancel-button="true"
              :in-input-area="true"
              @cancel="cancelReply" />
            <TipTapEditor
              ref="editor"
              v-model="message"
              :input-class="chatThreadStore.replyingToMessage ? 'c-chat__messageInput -replying' : 'c-chat__messageInput'"
              :enter-key-handler="enterKeyHandler"
              :placeholder="$t('chat.write_a_message')"
              @input="typingEvent"
              @paste-file="onPasteFile" />
          </div>
        </div>

        <transition-group
          name="actionsTransition"
          tag="div"
          class="o-flex -alignCenter -center -gap2 -fullHeight">
          <EmojiPicker
            v-if="editor"
            key="emoji-picker"
            :editor-ref="editor"
            :disabled="isMicrophoneListening" />
          <div
            v-if="!message"
            key="attachment-button">
            <button
              class="c-chat__inputButton"
              :disabled="chatAttachmentsStore.isUploading || isMicrophoneListening"
              @click="openFileUpload">
              <SvgIcon
                small
                type="chat_attachment" />
            </button>
            <input
              v-show="false"
              ref="chatMessageAttachmentInput"
              type="file"
              name="file"
              :disabled="isMicrophoneListening"
              @change="fileSelected" />
          </div>
          <div
            v-if="!message"
            key="microphone-button"
            class="c-chat__microphoneButtonWrapper">
            <MicrophoneTranscription
              @done="onVoiceRecordingDone"
              @set-listening-state="isMicrophoneListening = $event" />
          </div>
          <div
            v-if="message"
            key="send-button">
            <button
              class="c-chat__inputButton -send"
              @click="sendMessage">
              <SvgIcon
                small
                type="chat_send" />
              <span class="t-button1 c-chat__inputButtonText">{{ $t('chat.send') }}</span>
            </button>
          </div>
        </transition-group>
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  ref, watch, onMounted, onUnmounted, computed,
} from 'vue'

import EmojiPicker from '@/components/EmojiPicker.vue'
import TipTapEditor from '@/components/TipTapEditor.vue'
import constants from '@/constants'
import api from '@/logic/api'
import { getCustomEmojiIndex } from '@/logic/emoji/customEmojis'
import helpers from '@/logic/helpers'
import MicrophoneTranscription from '@/modules/askkeelearning/components/MicrophoneTranscription.vue'
import azureChatApi from '@/modules/chat/api/azureChat'
import chatApi from '@/modules/chat/api/chat'
import { useChatAttachmentsStore } from '@/modules/chat/chatAttachmentsStore'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import ReplyPreview from '@/modules/chat/components/ReplyPreview.vue'
import { detectLanguageForChunk } from '@/modules/chat/logic/chatMessageLanguageDetection'
import { getParticipant, getParticipantDisplayName } from '@/modules/chat/logic/chatThread'
import store from '@/store'

const editor = ref(null)
const actionContainer = ref(null)
const chatMessageAttachmentInput = ref(null)
const message = ref('')
const isTyping = ref(false)
const chatStore = useChatStore()
const chatThreadStore = useChatThreadStore(chatStore.chatThread.azure_chat_thread_id)
const chatAttachmentsStore = useChatAttachmentsStore(chatStore.chatThread.azure_chat_thread_id)
const isMicrophoneListening = ref(false)

let timeoutIdForTyping = null
let intervalIdForRequest = null
let chatActionsResizeObserver = null

const otherDisplayName = computed(() => {
  return getParticipant(chatStore.chatThread)?.displayName ?? ''
})

const isInactive = computed(() => {
  return chatStore.chatThread?.active === false
})

const replyUsername = computed(() => {
  if (!chatThreadStore.replyingToMessage) {
    return ''
  }
  const senderId = parseInt(chatThreadStore.replyingToMessage.senderDisplayName)
  if (senderId === store.getters['auth/userId']) {
    return api.i18n.t('chat.you')
  }
  return getParticipantDisplayName(chatStore.chatThread)
})

function cancelReply() {
  chatThreadStore.replyingToMessage = null
}

const openFileUpload = () => {
  if (isInactive.value) {
    return
  }
  chatMessageAttachmentInput.value.click()
}
const fileSelected = async (event) => {
  try {
    if (event.target.files.length) {
      await chatStore.fileUploadRef?.getFilePond().addFile(event.target.files[0])
      // allow the same file to be chosen again
      chatMessageAttachmentInput.value.value = ''
      await chatStore.fileUploadRef?.getFilePond().processFile()
    }
  } catch (error) {
    store.commit('notifications/addError', {
      title: api.i18n.t('notifications.failed_to_send_message'),
    })
  }
}
const onVoiceRecordingDone = async (payload) => {
  try {
    const { audioBlob, transcription } = payload || {}
    if (isInactive.value) {
      return
    }
    if (!audioBlob) {
      return
    }
    const mime = audioBlob.type || 'audio/webm'
    let ext = 'webm'
    if (mime.includes('ogg')) {
      ext = 'ogg'
    } else if (mime.includes('mp4')) {
      ext = 'mp4'
    }
    const fileName = `voice-recording.${ext}`
    const file = new File([audioBlob], fileName, { type: mime })
    await chatStore.fileUploadRef?.getFilePond().addFile(file, { metadata: { voice_recording: true, transcript: transcription } })
    await chatStore.fileUploadRef?.getFilePond().processFile()
  } catch (error) {
    store.commit('notifications/addError', { title: api.i18n.t('notifications.failed_to_send_message') })
  }
}
function typingEvent() {
  isTyping.value = true

  clearTimeout(timeoutIdForTyping)
  timeoutIdForTyping = setTimeout(() => {
    isTyping.value = false
  }, 2000)
}

watch(isTyping, async () => {
  if (!isTyping.value) {
    clearInterval(intervalIdForRequest)
    return
  }

  intervalIdForRequest = setInterval(() => {
    if (isTyping.value) {
      azureChatApi.setTypingEvent(chatStore.chatThread.azure_chat_thread_id)
    }
  }, 1000)
})

const isMessageContainsUrl = (text) => {
  const content = text.trim()
  const strippedContent = content.replace(/<[^>]*>?/gm, '').trim()
  if (!strippedContent) {
    return false
  }

  const urlRegex = /(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)|(www\.[\w\-._~:/?#[\]@!$&'()*+,;=%]+)|([\da-z.-]+\.[a-z.]{2,6}[/?][\w\-._~:/?#[\]@!$&'()*+,;=%]*)/i

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  // If the entire stripped content is just an email, don't treat it as a URL
  if (emailRegex.test(strippedContent)) {
    return false
  }

  return urlRegex.test(strippedContent)
}

function maybeTriggerNotificationsPrompt() {
  if (window?.Capacitor) {
    return
  }
  // Only trigger if not already granted and user hasn't dismissed previously
  if (Notification?.permission !== 'granted' && !chatStore.chatNotificationPromptDismissed) {
    chatStore.triggerNotificationPromptAfterSend()
  }
}

// Custom emoji IDs to restrict replacements to known custom emojis only
const customEmojiIndex = getCustomEmojiIndex()
const customEmojiIdSet = new Set(Object.keys(customEmojiIndex))

// Convert TipTap editor HTML to plain text for sending, replacing custom emoji images
// (which have alt=":id:") with their shortcode ":id:" — only for IDs in customEmojiIds
const toShortcodesForSend = (html) => {
  if (!html) {
    return ''
  }
  const container = document.createElement('div')
  container.innerHTML = html

  // Replace <img alt=":id:"> with ":id:"
  container.querySelectorAll('img[alt]').forEach((img) => {
    const alt = img.getAttribute('alt') || ''
    const match = alt.match(/^:([^:\s]+):$/)
    const id = match && match[1]
    if (id && customEmojiIdSet.has(id)) {
      // Replace with shortcode :id:
      img.replaceWith(document.createTextNode(`:${id}:`))
    }
  })

  // Convert <br> to newlines so backend receives line breaks as text
  container.querySelectorAll('br').forEach((br) => {
    br.replaceWith(document.createTextNode('\n'))
  })

  // Extract text content
  return container.textContent || ''
}

const focusMessageInput = () => {
  // We need to delay focus to allow the DOM to update until isInactive value changes
  setTimeout(() => {
    editor.value?.editor.chain().focus()
  }, 100)
}

watch(() => chatThreadStore.replyingToMessage, (newValue) => {
  if (newValue) {
    focusMessageInput()
  }
  // since the reply preview is added to the DOM(In chatMessageActions), we need to update the messages container height
  chatStore.updateMessagesContainerHeight()
})

const sendMessage = async () => {
  if (isInactive.value) {
    return
  }
  if (!message.value) {
    return
  }
  let replyingToMessage = chatThreadStore.replyingToMessage

  async function processMessageChunk(messageChunk) {
    const metadata = {
      type: 'text',
      state: constants.CHAT.STATES.SENDING,
    }
    if (replyingToMessage) {
      metadata.replyToMessageId = replyingToMessage.id.toString()
    }
    if (isMessageContainsUrl(messageChunk)) {
      metadata.type = 'link'
    }
    const messageId = new Date().valueOf().toString()
    metadata.messageId = messageId

    // Detect language of the message chunk and store in metadata
    try {
      const chosenLanguage = await detectLanguageForChunk(messageChunk, chatThreadStore)
      if (chosenLanguage) {
        metadata.language = chosenLanguage
      }
    } catch (e) {
      // Its fine to ignore detection errors. The only consequence will be that the other user cant translate this message.
      // eslint-disable-next-line no-console
      console.error(e)
    }

    chatThreadStore.messages.push({
      id: +messageId,
      content: messageChunk,
      senderDisplayName: store.getters['auth/userId'],
      createdOn: new Date(),
      metadata: { ...metadata },
    })
    await chatThreadStore.updateSortedMessages()
    chatThreadStore.scrollContainerDown()

    try {
      metadata.state = constants.CHAT.STATES.SENT
      const sendMessageResult = await azureChatApi.sendMessage(chatStore.chatThread.azure_chat_thread_id, messageChunk, metadata)
      chatApi.notifyAboutNewMessage({
        body: messageChunk,
        chatThreadId: chatStore.chatThread.id,
        participantId: getParticipant(chatStore.chatThread)?.user_id,
        messageId: sendMessageResult.id,
      })
      azureChatApi.setReadingEvent(chatStore.chatThread.azure_chat_thread_id, sendMessageResult.id)
      chatStore.setChatThreadLastMessage(chatStore.chatThread.azure_chat_thread_id)
    } catch (error) {
      metadata.state = constants.CHAT.STATES.ERROR
      metadata.error_status = constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SEND
      const messageIdx = chatThreadStore.messages.findIndex((threadMessage) => threadMessage.metadata?.messageId && +threadMessage.metadata.messageId === +metadata.messageId)
      if (messageIdx !== -1) {
        chatThreadStore.messages[messageIdx].metadata = metadata
      }
      await chatThreadStore.updateSortedMessages()
      store.commit('notifications/addError', {
        title: api.i18n.t('notifications.failed_to_send_message'),
      })
    }
  }

  const maxMessageLength = 20000
  const messageChunks = []
  // Transform editor HTML into a text string containing custom emoji shortcodes
  let remainingMessage = toShortcodesForSend(message.value)
  message.value = ''
  chatThreadStore.replyingToMessage = null
  focusMessageInput()

  // Split message into chunks if longer than 20000 characters
  while (remainingMessage.length > 0) {
    messageChunks.push(remainingMessage.slice(0, maxMessageLength))
    remainingMessage = remainingMessage.slice(maxMessageLength)
  }

  for (const messageChunk of messageChunks) {
    // we want to wait for the response to keep the correct order
    // eslint-disable-next-line no-await-in-loop
    await processMessageChunk(messageChunk)
  }
  isTyping.value = false
  replyingToMessage = null

  maybeTriggerNotificationsPrompt()
}

onMounted(() => {
  if (!isInactive.value && !helpers.isMobileViewport()) {
    focusMessageInput()
  }

  chatActionsResizeObserver = new ResizeObserver(() => {
    chatStore.updateMessagesContainerHeight()
  })
  chatActionsResizeObserver.observe(actionContainer.value)
})

const enterKeyHandler = async () => {
  await sendMessage()
}

onUnmounted(() => {
  clearInterval(intervalIdForRequest)
  clearTimeout(timeoutIdForTyping)
  if (chatActionsResizeObserver) {
    chatActionsResizeObserver.disconnect()
  }
})

const onPasteFile = async (file) => {
  try {
    if (isInactive.value) {
      return
    }
    if (!file) {
      return
    }
    await chatStore.fileUploadRef?.getFilePond().addFile(file)
    // Process immediately to keep behavior consistent with fileSelected()
    await chatStore.fileUploadRef?.getFilePond().processFile()
  } catch (error) {
    store.commit('notifications/addError', { title: api.i18n.t('notifications.failed_to_send_message') })
  }
}
</script>

<style scoped>
.actionsTransition {
  backface-visibility: hidden;
  z-index: 1;
}

/* moving */
.actionsTransition-move {
  transition: all 200ms ease-in-out 50ms;
}

/* appearing */
.actionsTransition-enter-active {
  transition: all 100ms ease-out;
}

/* disappearing */
.actionsTransition-leave-active {
  transition: all 50ms ease-in;
  position: absolute;
  z-index: 0;
}

/* appear at / disappear to */
.actionsTransition-enter,
.actionsTransition-leave-to {
  opacity: 0;
}
</style>
